import { AxiosResponse } from 'axios';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import {
  findAccountingInformationReportRequestMap,
  findCancellationsRequestMap,
  findCancellationsResponseMap,
  findCashMovementReportRequestMap,
  findCashMovementReportResponseMap,
  findCashCountReportResponseMap,
  findCashDiscrepancyReportResponseMap,
  findClosingRequestMap,
  findClosingResponseMap,
  findClosingStatusTotalRequestMap,
  findDiscountsRequestMap,
  findDiscountsResponseMap,
  findExchangePurchaseReportResponseMap,
  findFixedFundReportResponseMap,
  findGCsByProductRequestMap,
  findGCsByProductResponseMap,
  findGcsByPaymentTypesRequestMap,
  findGcsByPaymentTypesResponseMap,
  findGcsByTaxRequestMap,
  findGcsByTaxResponseMap,
  findGcsRequestMap,
  findGcsResponseMap,
  findGrossSaleRequestMap,
  findHourRangeResponseMap,
  findMastersImportExecutionRequestMap,
  findMastersImportRequestMap,
  findMastersImportResponseMap,
  findMissingClosingCausesRequestMap,
  findMissingClosingCollectionsCausesResponseMap,
  findMissingClosingSalesCausesResponseMap,
  findMissingControlErrorsResponseMap,
  findMissingControlErrorsRequestMap,
  findMissingClosingTotalResponseMap,
  findOperationTypesResponseMap,
  findTransactionTypeRequestMap,
  findTransactionTypeResponseMap,
  findPaymentTypeRequestMap,
  findPaymentTypeResponseMap,
  findPendingInterfacesRequestMap,
  findPendingInterfacesResponseMap,
  findInterfacesRequestMap,
  findInterfacesResponseMap,
  findPettyCashDifferenceByPaymentTypeRequestMap,
  findPettyCashDifferenceByPaymentTypeResponseMap,
  findPettyCashDifferenceRequestMap,
  findPettyCashDifferenceResponseMap,
  findPettyCashWithdrawalReportResponseMap,
  findExtraCashReportResponseMap,
  findPosRequestMap,
  findPosResponseMap,
  findOperatorDlvRequestMap,
  findOperatorDlvResponseMap,
  findRecoveryReportResponseMap,
  findReliefsRequestMap,
  findReliefsResponseMap,
  findReportTenderRequestMap,
  findReportTenderResponseMap,
  findSalesByProductRequestMap,
  findSalesByProductResponseMap,
  findSalesJournalFcsResponseMap,
  findSalesJournalGcsResponseMap,
  findSalesJournalNcsResponseMap,
  findSalesJournalRequestMap,
  findSalesJournalThirdPartyResponseMap,
  findSalesRequestMap,
  findSalesResponseMap,
  findSalesTypesRequestMap,
  findSalesTypesResponseMap,
  findThirdPartySalesByItemsRequestMap,
  findThirdPartySalesByItemsResponseMap,
  findTimeBandRequestMap,
  findTimeBandResponseMap,
  getCountriesForPermissions,
  getSelectedEndDate,
  getSelectedStartDate,
  hasIncompleteRanges,
  noValidDateRanges,
  createPendingInterfacesRequestMap,
  executionPendingInterfacesRequestMap,
  executionPendingInterfacesResponseMap,
  findPendingInterfacesParametersResponseMap,
  findEconomicAccountingReportRequestMap,
  findEconomicAccountingReportResponseMap,
  findEnvelopeDetailsReportRequestMap,
  findEnvelopeDetailsReportResponseMap,
  constants,
} from '../../helpers';
import {
  AppStorage,
  CollectionsApi,
  CollectionsReportsApi,
  FindCancellationsResponse,
  FindCashMovementReportResponse,
  FindCashCountReportResponse,
  FindCashDiscrepancyReportResponse,
  FindClosingResponse,
  FindEnvelopeDetailsReportResponse,
  FindDiscountsResponse,
  FindExchangePurchaseReportResponse,
  FindFixedFundReportResponse,
  FindGCsByProductResponse,
  FindGcsByPaymentTypesResponse,
  FindGcsByTaxResponse,
  FindGcsResponse,
  FindGrossSaleResponse,
  FindMastersImportResponse,
  FindMastersTypesResponse,
  FindMissingClosingCausesResponse,
  FindOperationTypesResponse,
  FindTransactionTypeResponse,
  FindPaymentTypesResponse,
  FindPendingInterfacesResponse,
  FindInterfacesResponse,
  FindPettyCashDifferenceResponse,
  FindEconomicAccountingReportResponse,
  FindPettyCashWithdrawalReportResponse,
  FindExtraCashReportResponse,
  FindPosResponse,
  FindOperatorDlvResponse,
  FindRecoveryReportResponse,
  FindReportTenderResponse,
  FindSalesByProductResponse,
  FindSalesJournalResponse,
  FindSalesResponse,
  FindSalesTypesResponse,
  FindThirdPartySalesByItemsResponse,
  FindTimeBandResponse,
  SalesApi,
  SalesParametersApi,
  ExecutionPendingInterfacesResponse,
  FindPendingInterfacesParametersResponse,
  FindMissingControlErrorsResponse,
  MissingControlApi,
} from '../../services';
import {
  AppPermissions,
  CancellationsFilter,
  CausesFilter,
  ClosingFilter,
  ClosingViewMode,
  CollectionsFilter,
  Country,
  DateValue,
  FindClosingStatusResponse,
  GcsFilter,
  HttpRequestError,
  MasterData,
  MastersImportDetailFilter,
  MissingCauses,
  OpStructuresFilter,
  InterfacesFilter,
  InterfacesParameters,
  PendingInterfacesFilter,
  Permission,
  ReportFilter,
  Stores,
  ExecutionStatusPendingInterfacesFilter,
  TransactionType,
} from '../../types';
import {
  activeCountriesSelector,
  masterDataDataSelector,
  reportCancellationsFilterSelector,
  reportCausesFilterSelector,
  reportClosingFilterSelector,
  reportCollectionsFilterSelector,
  reportFilterCountryCodeSelector,
  reportFilterEndBusinessDateSelector,
  reportFilterStartBusinessDateSelector,
  reportFilterStoresSelector,
  reportFilterSelector,
  reportGcsFilterSelector,
  reportMastersImportFilterSelector,
  reportMissingClosingTotalFilteredStoresSelector,
  reportOpStructuresFilterSelector,
  reportInterfacesFilterSelector,
  reportPendingInterfacesFilterSelector,
  userPermissionsDataSelector,
  reportExecutePendingInterfacesFilterSelector,
  reportCollectionsFilterEconomicAccountingSelector,
  reportAccountingInformationFilterSelector,
  reportFilterTransactionTypeByProductSelector,
} from '../selectors';
import {
  findCancellationsFailure,
  findCancellationsSuccess,
  findCashMovementReportSuccess,
  findCashMovementReportFailure,
  findCauseTypesFailure,
  findCauseTypesSuccess,
  findCashCountReportSuccess,
  findCashCountReportFailure,
  findCashDiscrepancyReportSuccess,
  findCashDiscrepancyReportFailure,
  findClosingCollectionsFailure,
  findClosingCollectionsSuccess,
  findClosingSalesFailure,
  findClosingSalesSuccess,
  findExchangePurchaseReportSuccess,
  findExchangePurchaseReportFailure,
  findFixedFundReportSuccess,
  findFixedFundReportFailure,
  findEnvelopeDetailsReportSuccess,
  findEnvelopeDetailsReportFailure,
  findGCsByProductFailure,
  findGCsByProductSuccess,
  findGcsByPaymentTypesFailure,
  findGcsByPaymentTypesSuccess,
  findGcsByTaxFailure,
  findGcsByTaxSuccess,
  findGcsFailure,
  findGcsSuccess,
  findHourRangeFailure,
  findHourRangeRequested,
  findHourRangeSuccess,
  findItemDiscountsFailure,
  findItemDiscountsRequested,
  findItemDiscountsSuccess,
  findMastersImportFailure,
  findMastersImportSuccess,
  findMastersTypesFailure,
  findMastersTypesSuccess,
  findMissingClosingCollectionsCausesFailure,
  findMissingClosingCollectionsCausesSuccess,
  findMissingControlErrorsFailure,
  findMissingControlErrorsSuccess,
  findMissingClosingSalesCausesFailure,
  findMissingClosingSalesCausesSuccess,
  findMissingClosingTotalFailure,
  findMissingClosingTotalRequested,
  findMissingClosingTotalSuccess,
  findInterfacesFailure,
  findInterfacesSuccess,
  findInterfacesParametersFailure,
  findInterfacesParametersSuccess,
  findOperationTypesFailure,
  findOperationTypesSuccess,
  findTransactionTypeFailure,
  findTransactionTypeSuccess,
  findPaymentTypeFailure,
  findPaymentTypeRequested,
  findPaymentTypeSuccess,
  findPendingInterfacesSalesFailure,
  findPendingInterfacesSalesSuccess,
  findPettyCashDifferenceByPaymentTypeFailure,
  findPettyCashDifferenceByPaymentTypeSuccess,
  findPettyCashDifferenceFailure,
  findPettyCashDifferenceSuccess,
  findEconomicAccounitngReportSuccess,
  findEconomicAccounitngReportFailure,
  findPettyCashWithdrawalReportSuccess,
  findPettyCashWithdrawalReportFailure,
  findExtraCashReportSuccess,
  findExtraCashReportFailure,
  findPosFailure,
  findPosRequested,
  findPosSuccess,
  findOperatorDlvFailure,
  findOperatorDlvRequested,
  findOperatorDlvSuccess,
  findRecoveryReportSuccess,
  findRecoveryReportFailure,
  findReliefsFailure,
  findReliefsSuccess,
  findReportTenderFailure,
  findReportTenderSuccess,
  findSaleDiscountsFailure,
  findSaleDiscountsRequested,
  findSaleDiscountsSuccess,
  findSalesByProductFailure,
  findSalesByProductSuccess,
  findSalesFailure,
  findSalesJournalFcsFailure,
  findSalesJournalFcsSuccess,
  findSalesJournalGcsFailure,
  findSalesJournalGcsSuccess,
  findSalesJournalNcsFailure,
  findSalesJournalNcsSuccess,
  findSalesJournalThirdPartyFailure,
  findSalesJournalThirdPartySuccess,
  findSalesSuccess,
  findSalesTypesFailure,
  findSalesTypesRequested,
  findSalesTypesSuccess,
  findThirdPartySalesByItemsFailure,
  findThirdPartySalesByItemsSuccess,
  findTimeBandFailure,
  findTimeBandRequested,
  findTimeBandSuccess,
  mastersImportExecutionFailure,
  mastersImportExecutionSuccess,
  pendingInterfacesCreateSuccess,
  pendingInterfacesCreateFailure,
  executePendingInterfacesSuccess,
  executePendingInterfacesFailure,
  executePendingInterfacesParametersSuccess,
  executePendingInterfacesParametersFailure,
} from './actionCreators';
import {
  ActionType,
  FindCancellationsRequestedAction,
  FindCashMovementReportRequestedAction,
  FindCashCountReportRequestedAction,
  FindCashDiscrepancyReportRequestedAction,
  FindClosingCollectionsRequestedAction,
  FindClosingSalesRequestedAction,
  FindExchangePurchaseReportRequestedAction,
  FindFixedFundReportRequestedAction,
  FindEnvelopeDetailsReportRequestedAction,
  FindGCsByProductRequestedAction,
  FindGcsByPaymentTypesRequestedAction,
  FindGcsByTaxRequestedAction,
  FindGcsRequestedAction,
  FindMissingClosingTotalRequestedAction,
  FindInterfacesRequestedAction,
  FindPendingInterfacesSalesRequestedAction,
  FindPettyCashDifferenceByPaymentTypeRequestedAction,
  FindPettyCashDifferenceRequestedAction,
  FindPettyCashWithdrawalReportRequestedAction,
  FindExtraCashReportRequestedAction,
  FindEconomicAccounitngReportRequestedAction,
  FindRecoveryReportRequestedAction,
  FindReportTenderRequestedAction,
  FindSalesByProductRequestedAction,
  FindSalesRequestedAction,
  FindThirdPartySalesByItemsRequestedAction,
  MastersImportExecutionRequestedAction,
  PendingInterfacesCreateRequestedAction,
  FindReliefsRequestedAction,
} from './types';

import { enqueueNotification } from '..';
import {
  FindPettyCashDifferenceByPaymentTypeResponse,
  FindReliefsResponse,
} from '../../services/CollectionsReportsApi';

function* notifyOnedriveSuccess() {
  yield put(
    enqueueNotification({
      key: 'sendOnedriveSuccess',
      message: t => t('shared.messages.sendToOnedrive'),
      variant: 'success',
    })
  );
}
const DATE_FILTER_ACTION = [
  ActionType.changeFilterStartAccountingDate,
  ActionType.changeFilterEndAccountingDate,
  ActionType.changeFilterStartBusinessDate,
  ActionType.changeFilterEndBusinessDate,
  ActionType.changeFilterStartOperationDate,
  ActionType.changeFilterEndOperationDate,
];

const ANALYTICS_OPENED_ACTIONS = {
  [ActionType.findSalesRequested]: 'sales',
  [ActionType.findGcsRequested]: 'gcs',
  [ActionType.findGcsByPaymentTypesRequested]: 'gcsByPaymentTypes',
  [ActionType.findGcsByTaxRequested]: 'gcsByTax',
  [ActionType.findClosingSalesRequested]: 'closingSales',
  [ActionType.findClosingCollectionsRequested]: 'closingCollections',
  [ActionType.findMissingClosingSalesCausesRequested]: 'missingClosingSalesCauses',
  [ActionType.findMissingClosingCollectionsCausesRequested]: 'missingClosingCollectionsCauses',
  [ActionType.findMissingControlErrorsRequested]: 'missingControlErrors',
  [ActionType.findMissingClosingTotalRequested]: 'missingClosingTotal',
  [ActionType.findReportTenderRequested]: 'reportTender',
  [ActionType.findReliefsRequested]: 'reliefs',
  [ActionType.findCancellationsRequested]: 'cancellations',
  [ActionType.findSalesByProductRequested]: 'salesByProduct',
  [ActionType.findGCsByProductRequested]: 'gcsByProduct',
  [ActionType.findThirdPartySalesByItemsRequested]: 'thirdPartySalesByItems',
  [ActionType.findPettyCashDifferenceRequested]: 'pettyCashDifference',
  [ActionType.findPettyCashDifferenceByPaymentTypeRequested]: 'pettyCashDifferenceByPaymentType',
  [ActionType.findEconomicAccounitngReportRequested]: 'economicAccounting',
  [ActionType.findCashMovementReportRequested]: 'cashMovement',
  [ActionType.findCashCountReportRequested]: 'cashCount',
  [ActionType.findCashDiscrepancyReportRequested]: 'cashDiscrepancy',
  [ActionType.findExchangePurchaseReportRequested]: 'exchangePurchase',
  [ActionType.findFixedFundReportRequested]: 'fixedFund',
  [ActionType.findPettyCashWithdrawalReportRequested]: 'pettyCashWithdrawal',
  [ActionType.findExtraCashReportRequested]: 'extraCash',
  [ActionType.findRecoveryReportRequested]: 'recovery',
  [ActionType.findEnvelopeDetailsReportRequested]: 'envelopeDetails',
  [ActionType.findSalesJournalRequested]: 'salesJournal',
  [ActionType.findInterfacesRequested]: 'interfaces',
  [ActionType.findPendingInterfacesSalesRequested]: 'pendingInterfaces',
  [ActionType.pendingInterfacesCreateRequested]: 'pendingInterfacesCreate',
  [ActionType.executePendingInterfacesRequested]: 'pendingInterfacesExecution',
  [ActionType.mastersImportExecutionRequested]: 'mastersImportExecution',
};

const ANALYTICS_SUCCESS_ACTIONS = {
  [ActionType.findSalesSuccess]: 'sales',
  [ActionType.findGcsSuccess]: 'gcs',
  [ActionType.findGcsByPaymentTypesSuccess]: 'gcsByPaymentTypes',
  [ActionType.findGcsByTaxSuccess]: 'gcsByTax',
  [ActionType.findClosingSalesSuccess]: 'closingSales',
  [ActionType.findClosingCollectionsSuccess]: 'closingCollections',
  [ActionType.findMissingClosingSalesCausesSuccess]: 'missingClosingSalesCauses',
  [ActionType.findMissingClosingCollectionsCausesSuccess]: 'missingClosingCollectionsCauses',
  [ActionType.findMissingControlErrorsSuccess]: 'missingControlErrors',
  [ActionType.findMissingClosingTotalSuccess]: 'missingClosingTotal',
  [ActionType.findReportTenderSuccess]: 'reportTender',
  [ActionType.findReliefsSuccess]: 'reliefs',
  [ActionType.findCancellationsSuccess]: 'cancellations',
  [ActionType.findSalesByProductSuccess]: 'salesByProduct',
  [ActionType.findGCsByProductSuccess]: 'gcsByProduct',
  [ActionType.findThirdPartySalesByItemsSuccess]: 'thirdPartySalesByItems',
  [ActionType.findPettyCashDifferenceSuccess]: 'pettyCashDifference',
  [ActionType.findPettyCashDifferenceByPaymentTypeSuccess]: 'pettyCashDifferenceByPaymentType',
  [ActionType.findEconomicAccounitngReportSuccess]: 'economicAccounting',
  [ActionType.findCashMovementReportSuccess]: 'cashMovement',
  [ActionType.findCashCountReportSuccess]: 'cashCount',
  [ActionType.findCashDiscrepancyReportSuccess]: 'cashDiscrepancy',
  [ActionType.findExchangePurchaseReportSuccess]: 'exchangePurchase',
  [ActionType.findFixedFundReportSuccess]: 'fixedFund',
  [ActionType.findPettyCashWithdrawalReportSuccess]: 'pettyCashWithdrawal',
  [ActionType.findExtraCashReportSuccess]: 'extraCash',
  [ActionType.findRecoveryReportSuccess]: 'recovery',
  [ActionType.findEnvelopeDetailsReportSuccess]: 'envelopeDetails',
  [ActionType.findSalesJournalGcsSuccess]: 'salesJournal',
  [ActionType.findSalesJournalNcsSuccess]: 'salesJournal',
  [ActionType.findSalesJournalFcsSuccess]: 'salesJournal',
  [ActionType.findSalesJournalThirdPartySuccess]: 'salesJournal',
  [ActionType.findInterfacesSuccess]: 'interfaces',
  [ActionType.findPendingInterfacesSalesSuccess]: 'pendingInterfaces',
  [ActionType.pendingInterfacesCreateSuccess]: 'pendingInterfacesCreate',
  [ActionType.executePendingInterfacesSuccess]: 'pendingInterfacesExecution',
  [ActionType.mastersImportExecutionSuccess]: 'mastersImportExecution',
};

const ANALYTICS_FAILURE_ACTIONS = {
  [ActionType.findSalesFailure]: 'sales',
  [ActionType.findGcsFailure]: 'gcs',
  [ActionType.findGcsByPaymentTypesFailure]: 'gcsByPaymentTypes',
  [ActionType.findGcsByTaxFailure]: 'gcsByTax',
  [ActionType.findClosingSalesFailure]: 'closingSales',
  [ActionType.findClosingCollectionsFailure]: 'closingCollections',
  [ActionType.findMissingClosingSalesCausesFailure]: 'missingClosingSalesCauses',
  [ActionType.findMissingClosingCollectionsCausesFailure]: 'missingClosingCollectionsCauses',
  [ActionType.findMissingControlErrorsFailure]: 'missingControlErrors',
  [ActionType.findMissingClosingTotalFailure]: 'missingClosingTotal',
  [ActionType.findReportTenderFailure]: 'reportTender',
  [ActionType.findReliefsFailure]: 'reliefs',
  [ActionType.findCancellationsFailure]: 'cancellations',
  [ActionType.findSalesByProductFailure]: 'salesByProduct',
  [ActionType.findGCsByProductFailure]: 'gcsByProduct',
  [ActionType.findThirdPartySalesByItemsFailure]: 'thirdPartySalesByItems',
  [ActionType.findPettyCashDifferenceFailure]: 'pettyCashDifference',
  [ActionType.findPettyCashDifferenceByPaymentTypeFailure]: 'pettyCashDifferenceByPaymentType',
  [ActionType.findEconomicAccounitngReportFailure]: 'economicAccounting',
  [ActionType.findCashMovementReportFailure]: 'cashMovement',
  [ActionType.findCashCountReportFailure]: 'cashCount',
  [ActionType.findCashDiscrepancyReportFailure]: 'cashDiscrepancy',
  [ActionType.findExchangePurchaseReportFailure]: 'exchangePurchase',
  [ActionType.findFixedFundReportFailure]: 'fixedFund',
  [ActionType.findPettyCashWithdrawalReportFailure]: 'pettyCashWithdrawal',
  [ActionType.findExtraCashReportFailure]: 'extraCash',
  [ActionType.findRecoveryReportFailure]: 'recovery',
  [ActionType.findEnvelopeDetailsReportFailure]: 'envelopeDetails',
  [ActionType.findSalesJournalGcsFailure]: 'salesJournal',
  [ActionType.findSalesJournalNcsFailure]: 'salesJournal',
  [ActionType.findSalesJournalFcsFailure]: 'salesJournal',
  [ActionType.findSalesJournalThirdPartyFailure]: 'salesJournal',
  [ActionType.findInterfacesFailure]: 'interfaces',
  [ActionType.findPendingInterfacesSalesFailure]: 'pendingInterfaces',
  [ActionType.pendingInterfacesCreateFailure]: 'pendingInterfacesCreate',
  [ActionType.executePendingInterfacesFailure]: 'pendingInterfacesExecution',
  [ActionType.mastersImportExecutionFailure]: 'mastersImportExecution',
};

function trackAnalyticsEvent(
  eventType: 'OPENED' | 'EXECUTED' | 'EXPORTED' | 'FAILED',
  reportType: string
) {
  const trackingKey = `${eventType}:${reportType}`;
  return trackingKey;
  // TODO: temporarily disabled - POST /analytics commented out
  // try {
  //   const filters: ReportFilter = yield select(reportFilterSelector);
  //   yield call({ context: AnalyticsApi, fn: AnalyticsApi.trackReportEvent }, {
  //     report_type: reportType,
  //     event_type: eventType,
  //     filters,
  //     domain: 'sales',
  //   });
  // } catch (error) {
  //   // Fail-safe: analytics must never interrupt report UX.
  // }
}

function* trackOpened({ type }: { type: string }) {
  const reportType = ANALYTICS_OPENED_ACTIONS[type as keyof typeof ANALYTICS_OPENED_ACTIONS];
  if (reportType) {
    yield call(trackAnalyticsEvent, 'OPENED', reportType);
  }
}

function* trackSuccess({ type }: { type: string }) {
  const reportType = ANALYTICS_SUCCESS_ACTIONS[type as keyof typeof ANALYTICS_SUCCESS_ACTIONS];
  if (reportType) {
    yield call(trackAnalyticsEvent, 'EXECUTED', reportType);
    yield call(trackAnalyticsEvent, 'EXPORTED', reportType);
  }
}

function* trackFailure({ type }: { type: string }) {
  const reportType = ANALYTICS_FAILURE_ACTIONS[type as keyof typeof ANALYTICS_FAILURE_ACTIONS];
  if (reportType) {
    yield call(trackAnalyticsEvent, 'FAILED', reportType);
  }
}

function* findPos() {
  const filters: ReportFilter = yield select(reportFilterSelector);

  if (filters.stores.length !== 1 || noValidDateRanges(filters) || hasIncompleteRanges(filters))
    return;
  yield put(findPosRequested());
  try {
    const { data }: AxiosResponse<FindPosResponse> = yield call(
      SalesParametersApi.findPos,
      findPosRequestMap(
        filters.countryCode,
        filters.stores[0],
        getSelectedStartDate(filters) as Date,
        getSelectedEndDate(filters) as Date
      )
    );
    yield put(findPosSuccess(findPosResponseMap(data)));
  } catch (error) {
    yield put(findPosFailure(error as HttpRequestError));
  }
}

function* findOperatorDlv() {
  const filters: ReportFilter = yield select(reportFilterSelector);

  if (noValidDateRanges(filters) || hasIncompleteRanges(filters)) return;
  yield put(findOperatorDlvRequested());
  try {
    const { data }: AxiosResponse<FindOperatorDlvResponse> = yield call(
      SalesParametersApi.findOperatorDlv,
      findOperatorDlvRequestMap(
        filters.countryCode,
        getSelectedStartDate(filters) as Date,
        getSelectedEndDate(filters) as Date,
        filters.stores === undefined ? undefined : filters.stores[0]
      )
    );
    yield put(findOperatorDlvSuccess(findOperatorDlvResponseMap(data)));
  } catch (error) {
    yield put(findOperatorDlvFailure(error as HttpRequestError));
  }
}

function* findSalesTypes() {
  const { countryCode }: ReportFilter = yield select(reportFilterSelector);
  if (!countryCode) return;

  yield put(findSalesTypesRequested());
  try {
    const { data }: AxiosResponse<FindSalesTypesResponse> = yield call(
      SalesParametersApi.findSalesTypes,
      findSalesTypesRequestMap(countryCode)
    );
    yield put(findSalesTypesSuccess(findSalesTypesResponseMap(data)));
  } catch (error) {
    yield put(findSalesTypesFailure(error as HttpRequestError));
  }
}

function* findCauseTypes() {
  const { viewMode }: ClosingFilter = yield select(reportClosingFilterSelector);

  let causeTypes: MissingCauses[] = AppStorage.getCauseTypes(viewMode);

  try {
    if (causeTypes.length === 0) {
      if ([ClosingViewMode.COLLECTIONS, ClosingViewMode.CONSOLIDATED].includes(viewMode)) {
        const { data }: AxiosResponse<MissingCauses[]> = yield call(CollectionsApi.findCauseTypes);
        causeTypes.push(...data);
        AppStorage.setCauseTypes(ClosingViewMode.COLLECTIONS, data);
      }
      if ([ClosingViewMode.SALES, ClosingViewMode.CONSOLIDATED].includes(viewMode)) {
        const { data }: AxiosResponse<MissingCauses[]> = yield call(SalesApi.findCauseTypes);
        causeTypes.push(...data);
        AppStorage.setCauseTypes(ClosingViewMode.SALES, data);
      }
    }

    causeTypes = [...new Set(causeTypes)] as MissingCauses[];
    yield put(findCauseTypesSuccess(causeTypes));
  } catch (error) {
    yield put(findCauseTypesFailure(error as HttpRequestError));
  }
}

function* findPaymentType() {
  const { countries }: MasterData = yield select(masterDataDataSelector);
  const { countryCode }: ReportFilter = yield select(reportFilterSelector);
  const countryId = countries.find(i => i.countryCode === countryCode)?.countryId;

  if (!countryId) return;
  yield put(findPaymentTypeRequested());
  try {
    const { data }: AxiosResponse<FindPaymentTypesResponse> = yield call(
      SalesParametersApi.findPaymentTypes,
      findPaymentTypeRequestMap(countryId)
    );
    yield put(findPaymentTypeSuccess(findPaymentTypeResponseMap(data)));
  } catch (error) {
    yield put(findPaymentTypeFailure(error as HttpRequestError));
  }
}

function* findTimeBand() {
  const { countryCode }: ReportFilter = yield select(reportFilterSelector);
  if (!countryCode) return;

  yield put(findHourRangeRequested());
  yield put(findTimeBandRequested());

  try {
    const { data }: AxiosResponse<FindTimeBandResponse> = yield call(
      SalesParametersApi.findTimeBand,
      findTimeBandRequestMap(countryCode)
    );
    yield put(findHourRangeSuccess(findHourRangeResponseMap(data)));
    yield put(findTimeBandSuccess(findTimeBandResponseMap(data)));
  } catch (error) {
    yield put(findHourRangeFailure(error as HttpRequestError));
    yield put(findTimeBandFailure(error as HttpRequestError));
  }
}

function* findSales(action: FindSalesRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const stores: Stores[] = yield select(reportMissingClosingTotalFilteredStoresSelector);
  const opStructuresFilters: OpStructuresFilter = yield select(reportOpStructuresFilterSelector);
  try {
    yield put(
      findMissingClosingTotalRequested(
        getSelectedStartDate(filters) as Date,
        getSelectedEndDate(filters) as Date,
        filters.countryCode,
        filters.stores.length > 0 ? filters.stores : stores
      )
    );
    const { data, status }: AxiosResponse<FindSalesResponse> = yield call(
      SalesApi.findSales,
      findSalesRequestMap(filters, opStructuresFilters, action.sentToOnedrive)
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findSalesSuccess([]));
    } else {
      yield put(findSalesSuccess(findSalesResponseMap(data)));
    }
  } catch (error) {
    yield put(findSalesFailure(error as HttpRequestError));
  }
}

function* findTenderReport(action: FindReportTenderRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const stores: Stores[] = yield select(reportMissingClosingTotalFilteredStoresSelector);
  const opStructuresFilters: OpStructuresFilter = yield select(reportOpStructuresFilterSelector);
  try {
    if (!constants.isSmsReportedCountry(filters.countryCode)) {
      yield put(
        findMissingClosingTotalRequested(
          getSelectedStartDate(filters) as Date,
          getSelectedEndDate(filters) as Date,
          filters.countryCode,
          filters.stores.length > 0 ? filters.stores : stores
        )
      );
    }
    const { data, status }: AxiosResponse<FindReportTenderResponse> = yield call(
      SalesApi.findReportTender,
      findReportTenderRequestMap(filters, opStructuresFilters, action.sendToOnedrive)
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findReportTenderSuccess([]));
    } else {
      yield put(findReportTenderSuccess(findReportTenderResponseMap(data)));
    }
  } catch (error) {
    yield put(findReportTenderFailure(error as HttpRequestError));
  }
}

function* findGcs(action: FindGcsRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  try {
    const { data, status }: AxiosResponse<FindGcsResponse> = yield call(
      SalesApi.findGcs,
      findGcsRequestMap(filters, action.sendToOndrive)
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findGcsSuccess([]));
    } else {
      yield put(findGcsSuccess(findGcsResponseMap(data)));
    }
  } catch (error) {
    yield put(findGcsFailure(error as HttpRequestError));
  }
}

function* findGcsByPaymentTypes(action: FindGcsByPaymentTypesRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);

  try {
    const { data, status }: AxiosResponse<FindGcsByPaymentTypesResponse> = yield call(
      SalesApi.findGcsByPaymentTypes,
      findGcsByPaymentTypesRequestMap(filters, action.sendToOndrive)
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findGcsByPaymentTypesSuccess([]));
    } else {
      yield put(findGcsByPaymentTypesSuccess(findGcsByPaymentTypesResponseMap(data)));
    }
  } catch (error) {
    yield put(findGcsByPaymentTypesFailure(error as HttpRequestError));
  }
}

function* findGcsByTax(action: FindGcsByTaxRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  try {
    const { data, status }: AxiosResponse<FindGcsByTaxResponse> = yield call(
      SalesApi.findGcsByTax,
      findGcsByTaxRequestMap(filters, action.sendToOndrive)
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findGcsByTaxSuccess([]));
    } else {
      yield put(findGcsByTaxSuccess(findGcsByTaxResponseMap(data)));
    }
  } catch (error) {
    yield put(findGcsByTaxFailure(error as HttpRequestError));
  }
}

function* findClosingSales({
  sentToOnedrive,
  startDate,
  endDate,
}: FindClosingSalesRequestedAction) {
  try {
    const filters: ClosingFilter = yield select(reportClosingFilterSelector);
    const opStructuresFilters: OpStructuresFilter = yield select(reportOpStructuresFilterSelector);
    const { data: activeCountries } = yield select(activeCountriesSelector);
    const permissions: Permission[] = yield select(userPermissionsDataSelector);
    const countriesForPermissions = getCountriesForPermissions(
      [AppPermissions.MISSING_CLOSING_DETAIL],
      permissions,
      activeCountries
    );
    const countries =
      filters.countries.length > 0
        ? filters.countries.map(i => i.countryCode)
        : countriesForPermissions.map(i => i.countryCode);
    const start = startDate || (filters.startBusinessDate.accept as Date);
    const end = endDate || (filters.endBusinessDate.accept as Date);

    const { data, status }: AxiosResponse<FindClosingResponse> = yield call(
      SalesApi.findClosing,
      findClosingRequestMap(
        countries,
        filters.stores,
        start,
        end,
        filters.causeTypes,
        filters.federativeEntities,
        filters.franchiseTypes,
        sentToOnedrive,
        opStructuresFilters.regionalMgmt.map(i => i.regionalMgmtId),
        opStructuresFilters.region.map(i => i.regionId),
        opStructuresFilters.management.map(i => i.mgmtId),
        opStructuresFilters.supervision.map(i => i.supervisionId),
        opStructuresFilters.agreement
          .map(i => i.agreementId)
          .filter((id): id is string => id !== null),
        opStructuresFilters.company.map(i => i.companyId)
      )
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findClosingSalesSuccess([], start, end));
    } else {
      yield put(findClosingSalesSuccess(findClosingResponseMap(data, 'sales'), start, end));
    }
  } catch (error) {
    yield put(findClosingSalesFailure(error as HttpRequestError));
  }
}

function* findClosingCollections({
  sentToOnedrive,
  startDate,
  endDate,
}: FindClosingCollectionsRequestedAction) {
  try {
    const filters: ClosingFilter = yield select(reportClosingFilterSelector);
    const opStructuresFilters: OpStructuresFilter = yield select(reportOpStructuresFilterSelector);
    const { data: activeCountries } = yield select(activeCountriesSelector);
    const permissions: Permission[] = yield select(userPermissionsDataSelector);
    const countriesForPermissions = getCountriesForPermissions(
      [AppPermissions.MISSING_CLOSING_DETAIL],
      permissions,
      activeCountries
    );
    const countries =
      filters.countries.length > 0
        ? filters.countries.map(i => i.countryCode)
        : countriesForPermissions.map(i => i.countryCode);
    const start = startDate || (filters.startBusinessDate.accept as Date);
    const end = endDate || (filters.endBusinessDate.accept as Date);

    const { data, status }: AxiosResponse<FindClosingResponse> = yield call(
      CollectionsApi.findClosing,
      findClosingRequestMap(
        countries,
        filters.stores,
        start,
        end,
        filters.causeTypes,
        filters.federativeEntities,
        filters.franchiseTypes,
        sentToOnedrive,
        opStructuresFilters.regionalMgmt.map(i => i.regionalMgmtId),
        opStructuresFilters.region.map(i => i.regionId),
        opStructuresFilters.management.map(i => i.mgmtId),
        opStructuresFilters.supervision.map(i => i.supervisionId),
        opStructuresFilters.agreement
          .map(i => i.agreementId)
          .filter((id): id is string => id !== null),
        opStructuresFilters.company.map(i => i.companyId)
      )
    );

    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findClosingCollectionsSuccess([], start, end));
    } else {
      yield put(
        findClosingCollectionsSuccess(findClosingResponseMap(data, 'collections'), start, end)
      );
    }
  } catch (error) {
    yield put(findClosingCollectionsFailure(error as HttpRequestError));
  }
}

function* findMissingClosingSalesCauses() {
  const causesFilters: CausesFilter = yield select(reportCausesFilterSelector);
  try {
    const { data }: AxiosResponse<FindMissingClosingCausesResponse> = yield call(
      SalesApi.findMissingClosingCauses,
      findMissingClosingCausesRequestMap(causesFilters)
    );
    yield put(findMissingClosingSalesCausesSuccess(findMissingClosingSalesCausesResponseMap(data)));
  } catch (error) {
    yield put(findMissingClosingSalesCausesFailure(error as HttpRequestError));
  }
}

function* findMissingClosingCollectionsCauses() {
  const causesFilters: CausesFilter = yield select(reportCausesFilterSelector);
  try {
    const { data }: AxiosResponse<FindMissingClosingCausesResponse> = yield call(
      CollectionsApi.findCollectionsMissingClosingCauses,
      findMissingClosingCausesRequestMap(causesFilters)
    );
    yield put(
      findMissingClosingCollectionsCausesSuccess(
        findMissingClosingCollectionsCausesResponseMap(data)
      )
    );
  } catch (error) {
    yield put(findMissingClosingCollectionsCausesFailure(error as HttpRequestError));
  }
}

function* findMissingControlErrors() {
  const causesFilters: CausesFilter = yield select(reportCausesFilterSelector);
  try {
    const { data }: AxiosResponse<FindMissingControlErrorsResponse> = yield call(
      MissingControlApi.findMissingControlErrors,
      findMissingControlErrorsRequestMap(causesFilters)
    );
    yield put(findMissingControlErrorsSuccess(findMissingControlErrorsResponseMap(data)));
  } catch (error) {
    yield put(findMissingControlErrorsFailure(error as HttpRequestError));
  }
}

function* findMissingClosingTotal(actions: FindMissingClosingTotalRequestedAction) {
  const { country, stores, dateFrom, dateTo } = actions;
  try {
    const { data }: AxiosResponse<FindClosingStatusResponse> = yield call(
      SalesApi.findClosingSalesStatus,
      findClosingStatusTotalRequestMap(dateFrom, dateTo, country, stores)
    );
    yield put(findMissingClosingTotalSuccess(findMissingClosingTotalResponseMap(data)));
  } catch (error) {
    yield put(findMissingClosingTotalFailure(error as HttpRequestError));
  }
}

function* findReliefs(action: FindReliefsRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const collectionsFilters: CollectionsFilter = yield select(reportCollectionsFilterSelector);
  const opStructuresFilters: OpStructuresFilter = yield select(reportOpStructuresFilterSelector);
  const { countries }: MasterData = yield select(masterDataDataSelector);
  const { countryId } = countries.find(i => i.countryCode === filters.countryCode) as Country;
  try {
    if (action.sentToOnedrive) {
      const { status }: AxiosResponse<FindReliefsResponse> = yield call(
        CollectionsReportsApi.findReliefs,
        findReliefsRequestMap(
          filters,
          collectionsFilters,
          opStructuresFilters,
          action.storesForCompany,
          action.sentToOnedrive
        )
      );
      if (status === 202) {
        yield notifyOnedriveSuccess();
      }
      yield put(findReliefsSuccess([]));
      return;
    }
    const { data }: AxiosResponse<FindPaymentTypesResponse> = yield call(
      SalesParametersApi.findPaymentTypes,
      findPaymentTypeRequestMap(countryId)
    );
    const localPaymentTypeId = data.find(i => i.payment_type === 'ML')?.payment_type_id;
    const [grossSaleResponse, ReliefsResponse]: [
      AxiosResponse<FindGrossSaleResponse>,
      AxiosResponse<FindReliefsResponse>
    ] = yield all([
      call(
        SalesApi.findGrossSale,
        findGrossSaleRequestMap(filters, collectionsFilters, localPaymentTypeId)
      ),
      call(
        CollectionsReportsApi.findReliefs,
        findReliefsRequestMap(
          filters,
          collectionsFilters,
          opStructuresFilters,
          action.storesForCompany
        )
      ),
    ]);
    yield put(
      findReliefsSuccess(
        findReliefsResponseMap(collectionsFilters, grossSaleResponse.data, ReliefsResponse.data)
      )
    );
  } catch (error) {
    yield put(findReliefsFailure(error as HttpRequestError));
  }
}

function* findCancellations(action: FindCancellationsRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const cancellationsFilters: CancellationsFilter = yield select(reportCancellationsFilterSelector);
  try {
    const { data, status }: AxiosResponse<FindCancellationsResponse> = yield call(
      SalesApi.findCancellations,
      findCancellationsRequestMap(filters, cancellationsFilters, action.sentToOnedrive)
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findCancellationsSuccess([]));
    } else {
      yield put(findCancellationsSuccess(findCancellationsResponseMap(data)));
    }
  } catch (error) {
    yield put(findCancellationsFailure(error as HttpRequestError));
  }
}

function* findOperationTypes() {
  try {
    let operationTypes = AppStorage.getOperationTypes();
    if (!operationTypes) {
      const { data }: AxiosResponse<FindOperationTypesResponse> = yield call(
        SalesParametersApi.findOperationTypes
      );
      operationTypes = findOperationTypesResponseMap(data);
    }
    yield put(findOperationTypesSuccess(operationTypes));
  } catch (error) {
    yield put(findOperationTypesFailure(error as HttpRequestError));
  }
}

function* findTransactionType() {
  const filters: ReportFilter = yield select(reportFilterSelector);
  try {
    let transactionType = AppStorage.getTransactionType();
    if (!transactionType) {
      const { data }: AxiosResponse<FindTransactionTypeResponse> = yield call(
        SalesParametersApi.findTransactionType,
        findTransactionTypeRequestMap(filters)
      );
      transactionType = findTransactionTypeResponseMap(data);
    }
    yield put(findTransactionTypeSuccess(transactionType));
  } catch (error) {
    yield put(findTransactionTypeFailure(error as HttpRequestError));
  }
}

function* findSalesByProduct(action: FindSalesByProductRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const stores: Stores[] = yield select(reportMissingClosingTotalFilteredStoresSelector);
  const opStructuresFilters: OpStructuresFilter = yield select(reportOpStructuresFilterSelector);
  try {
    yield put(
      findMissingClosingTotalRequested(
        getSelectedStartDate(filters) as Date,
        getSelectedEndDate(filters) as Date,
        filters.countryCode,
        filters.stores.length > 0 ? filters.stores : stores
      )
    );
    const { data, status }: AxiosResponse<FindSalesByProductResponse> = yield call(
      SalesApi.findSalesByProduct,
      findSalesByProductRequestMap(filters, opStructuresFilters, action.sendToOndrive)
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findSalesByProductSuccess([]));
    } else {
      yield put(findSalesByProductSuccess(findSalesByProductResponseMap(data)));
    }
  } catch (error) {
    yield put(findSalesByProductFailure(error as HttpRequestError));
  }
}

function* findGCsByProduct(action: FindGCsByProductRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const transactionTypeByProduct: TransactionType[] = yield select(
    reportFilterTransactionTypeByProductSelector
  );

  try {
    const { data, status }: AxiosResponse<FindGCsByProductResponse> = yield call(
      SalesApi.findGCsByProduct,
      findGCsByProductRequestMap(filters, action.sendToOndrive, transactionTypeByProduct)
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findGCsByProductSuccess([]));
    } else {
      yield put(findGCsByProductSuccess(findGCsByProductResponseMap(data)));
    }
  } catch (error) {
    yield put(findGCsByProductFailure(error as HttpRequestError));
  }
}

function* findThirdPartySalesByItems(action: FindThirdPartySalesByItemsRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  try {
    const { data, status }: AxiosResponse<FindThirdPartySalesByItemsResponse> = yield call(
      SalesApi.findThirdPartySalesByItems,
      findThirdPartySalesByItemsRequestMap(filters, action.sendToOndrive)
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findThirdPartySalesByItemsSuccess([]));
    } else {
      yield put(findThirdPartySalesByItemsSuccess(findThirdPartySalesByItemsResponseMap(data)));
    }
  } catch (error) {
    yield put(findThirdPartySalesByItemsFailure(error as HttpRequestError));
  }
}

function* findPettyCashDifference(action: FindPettyCashDifferenceRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const collectionsFilters: CollectionsFilter = yield select(reportCollectionsFilterSelector);
  const opStructuresFilters: OpStructuresFilter = yield select(reportOpStructuresFilterSelector);
  try {
    const { data, status }: AxiosResponse<FindPettyCashDifferenceResponse> = yield call(
      CollectionsReportsApi.findPettyCashDifference,
      findPettyCashDifferenceRequestMap(
        filters,
        collectionsFilters,
        action.sendToOndrive,
        opStructuresFilters,
        action.storesForCompany
      )
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findPettyCashDifferenceSuccess([]));
    } else {
      yield put(findPettyCashDifferenceSuccess(findPettyCashDifferenceResponseMap(data)));
    }
  } catch (error) {
    yield put(findPettyCashDifferenceFailure(error as HttpRequestError));
  }
}

function* findPettyCashDifferenceByPaymentType(
  action: FindPettyCashDifferenceByPaymentTypeRequestedAction
) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const collectionsFilters: CollectionsFilter = yield select(reportCollectionsFilterSelector);
  const opStructuresFilters: OpStructuresFilter = yield select(reportOpStructuresFilterSelector);
  try {
    const { data, status }: AxiosResponse<FindPettyCashDifferenceByPaymentTypeResponse> =
      yield call(
        CollectionsReportsApi.findPettyCashDifferenceByPaymentType,
        findPettyCashDifferenceByPaymentTypeRequestMap(
          filters,
          collectionsFilters,
          action.sendToOndrive,
          opStructuresFilters,
          action.storesForCompany
        )
      );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findPettyCashDifferenceByPaymentTypeSuccess([]));
    } else {
      yield put(
        findPettyCashDifferenceByPaymentTypeSuccess(
          findPettyCashDifferenceByPaymentTypeResponseMap(data)
        )
      );
    }
  } catch (error) {
    yield put(findPettyCashDifferenceByPaymentTypeFailure(error as HttpRequestError));
  }
}

function* findEconomicAccountingReport(action: FindEconomicAccounitngReportRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const collectionsFilters: CollectionsFilter = yield select(
    reportCollectionsFilterEconomicAccountingSelector
  );
  try {
    // const collectionsCleanFilters = { ...collectionsFilters }
    if (
      collectionsFilters.groupBy.length > 0 &&
      collectionsFilters.groupBy.includes('countryAcronym')
    )
      collectionsFilters.groupBy.splice(
        collectionsFilters.groupBy.findIndex(e => e === 'countryAcronym'),
        1
      );
    const { data, status }: AxiosResponse<FindEconomicAccountingReportResponse> = yield call(
      CollectionsReportsApi.findEconomicAccountigReport,
      findEconomicAccountingReportRequestMap(filters, collectionsFilters, action.sendToOndrive)
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findEconomicAccounitngReportSuccess([]));
    } else {
      yield put(findEconomicAccounitngReportSuccess(findEconomicAccountingReportResponseMap(data)));
    }
  } catch (error) {
    yield put(findEconomicAccounitngReportFailure(error as HttpRequestError));
  }
}

function* findCashMovementReport(action: FindCashMovementReportRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const collectionsFilters: CollectionsFilter = yield select(reportCollectionsFilterSelector);
  const opStructuresFilters: OpStructuresFilter = yield select(reportOpStructuresFilterSelector);
  try {
    const { data, status }: AxiosResponse<FindCashMovementReportResponse> = yield call(
      CollectionsReportsApi.findCashMovementReport,
      findCashMovementReportRequestMap(
        filters,
        collectionsFilters,
        action.sendToOndrive,
        opStructuresFilters,
        action.storesForCompany
      )
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findCashMovementReportSuccess([]));
    } else {
      yield put(findCashMovementReportSuccess(findCashMovementReportResponseMap(data)));
    }
  } catch (error) {
    yield put(findCashMovementReportFailure(error as HttpRequestError));
  }
}

function* findCashCountReport(action: FindCashCountReportRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const collectionsFilters: Pick<CollectionsFilter, 'startBusinessDate' | 'endBusinessDate'> =
    yield select(reportAccountingInformationFilterSelector);
  const opStructuresFilters: OpStructuresFilter = yield select(reportOpStructuresFilterSelector);
  try {
    const { data, status }: AxiosResponse<FindCashCountReportResponse> = yield call(
      CollectionsReportsApi.findCashCountReport,
      findAccountingInformationReportRequestMap(
        filters,
        collectionsFilters,
        action.sendToOndrive,
        opStructuresFilters,
        action.storesForCompany
      )
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findCashCountReportSuccess([]));
    } else {
      yield put(findCashCountReportSuccess(findCashCountReportResponseMap(data)));
    }
  } catch (error) {
    yield put(findCashCountReportFailure(error as HttpRequestError));
  }
}

function* findCashDiscrepancyReport(action: FindCashDiscrepancyReportRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const collectionsFilters: Pick<CollectionsFilter, 'startBusinessDate' | 'endBusinessDate'> =
    yield select(reportAccountingInformationFilterSelector);
  const opStructuresFilters: OpStructuresFilter = yield select(reportOpStructuresFilterSelector);
  try {
    const { data, status }: AxiosResponse<FindCashDiscrepancyReportResponse> = yield call(
      CollectionsReportsApi.findCashDiscrepancyReport,
      findAccountingInformationReportRequestMap(
        filters,
        collectionsFilters,
        action.sendToOndrive,
        opStructuresFilters,
        action.storesForCompany
      )
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findCashDiscrepancyReportSuccess([]));
    } else {
      yield put(findCashDiscrepancyReportSuccess(findCashDiscrepancyReportResponseMap(data)));
    }
  } catch (error) {
    yield put(findCashDiscrepancyReportFailure(error as HttpRequestError));
  }
}

function* findExchangePurchaseReport(action: FindExchangePurchaseReportRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const collectionsFilters: Pick<CollectionsFilter, 'startBusinessDate' | 'endBusinessDate'> =
    yield select(reportAccountingInformationFilterSelector);
  const opStructuresFilters: OpStructuresFilter = yield select(reportOpStructuresFilterSelector);
  try {
    const { data, status }: AxiosResponse<FindExchangePurchaseReportResponse> = yield call(
      CollectionsReportsApi.findExchangePurchaseReport,
      findAccountingInformationReportRequestMap(
        filters,
        collectionsFilters,
        action.sendToOndrive,
        opStructuresFilters,
        action.storesForCompany
      )
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findExchangePurchaseReportSuccess([]));
    } else {
      yield put(findExchangePurchaseReportSuccess(findExchangePurchaseReportResponseMap(data)));
    }
  } catch (error) {
    yield put(findExchangePurchaseReportFailure(error as HttpRequestError));
  }
}

function* findFixedFundReport(action: FindFixedFundReportRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const collectionsFilters: Pick<CollectionsFilter, 'startBusinessDate' | 'endBusinessDate'> =
    yield select(reportAccountingInformationFilterSelector);
  const opStructuresFilters: OpStructuresFilter = yield select(reportOpStructuresFilterSelector);
  try {
    const { data, status }: AxiosResponse<FindFixedFundReportResponse> = yield call(
      CollectionsReportsApi.findFixedFundReport,
      findAccountingInformationReportRequestMap(
        filters,
        collectionsFilters,
        action.sendToOndrive,
        opStructuresFilters,
        action.storesForCompany
      )
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findFixedFundReportSuccess([]));
    } else {
      yield put(findFixedFundReportSuccess(findFixedFundReportResponseMap(data)));
    }
  } catch (error) {
    yield put(findFixedFundReportFailure(error as HttpRequestError));
  }
}

function* findPettyCashWithdrawalReport(action: FindPettyCashWithdrawalReportRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const collectionsFilters: Pick<CollectionsFilter, 'startBusinessDate' | 'endBusinessDate'> =
    yield select(reportAccountingInformationFilterSelector);
  const opStructuresFilters: OpStructuresFilter = yield select(reportOpStructuresFilterSelector);
  try {
    const { data, status }: AxiosResponse<FindPettyCashWithdrawalReportResponse> = yield call(
      CollectionsReportsApi.findPettyCashWithdrawalReport,
      findAccountingInformationReportRequestMap(
        filters,
        collectionsFilters,
        action.sendToOndrive,
        opStructuresFilters,
        action.storesForCompany
      )
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findPettyCashWithdrawalReportSuccess([]));
    } else {
      yield put(
        findPettyCashWithdrawalReportSuccess(findPettyCashWithdrawalReportResponseMap(data))
      );
    }
  } catch (error) {
    yield put(findPettyCashWithdrawalReportFailure(error as HttpRequestError));
  }
}

function* findExtraCashReport(action: FindExtraCashReportRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const collectionsFilters: Pick<CollectionsFilter, 'startBusinessDate' | 'endBusinessDate'> =
    yield select(reportAccountingInformationFilterSelector);
  const opStructuresFilters: OpStructuresFilter = yield select(reportOpStructuresFilterSelector);
  try {
    const { data, status }: AxiosResponse<FindExtraCashReportResponse> = yield call(
      CollectionsReportsApi.findExtraCashReport,
      findAccountingInformationReportRequestMap(
        filters,
        collectionsFilters,
        action.sendToOndrive,
        opStructuresFilters,
        action.storesForCompany
      )
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findExtraCashReportSuccess([]));
    } else {
      yield put(findExtraCashReportSuccess(findExtraCashReportResponseMap(data)));
    }
  } catch (error) {
    yield put(findExtraCashReportFailure(error as HttpRequestError));
  }
}

function* findRecoveryReport(action: FindRecoveryReportRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const collectionsFilters: Pick<CollectionsFilter, 'startBusinessDate' | 'endBusinessDate'> =
    yield select(reportAccountingInformationFilterSelector);
  const opStructuresFilters: OpStructuresFilter = yield select(reportOpStructuresFilterSelector);
  try {
    const { data, status }: AxiosResponse<FindRecoveryReportResponse> = yield call(
      CollectionsReportsApi.findRecoveryReport,
      findAccountingInformationReportRequestMap(
        filters,
        collectionsFilters,
        action.sendToOndrive,
        opStructuresFilters,
        action.storesForCompany
      )
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findRecoveryReportSuccess([]));
    } else {
      yield put(findRecoveryReportSuccess(findRecoveryReportResponseMap(data)));
    }
  } catch (error) {
    yield put(findRecoveryReportFailure(error as HttpRequestError));
  }
}

function* findEnvelopeDetailsReport(action: FindEnvelopeDetailsReportRequestedAction) {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const collectionsFilters: CollectionsFilter = yield select(reportCollectionsFilterSelector);
  try {
    const { data, status }: AxiosResponse<FindEnvelopeDetailsReportResponse> = yield call(
      CollectionsReportsApi.findEnvelopeDetailsReport,
      findEnvelopeDetailsReportRequestMap(
        filters,
        collectionsFilters,
        action.movementCode,
        action.sentToOnedrive
      )
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findEnvelopeDetailsReportSuccess([]));
    } else {
      yield put(findEnvelopeDetailsReportSuccess(findEnvelopeDetailsReportResponseMap(data)));
    }
  } catch (error) {
    yield put(findEnvelopeDetailsReportFailure(error as HttpRequestError));
  }
}

function* findSalesJournalGcs() {
  const filters: ReportFilter = yield select(reportFilterSelector);
  try {
    const { data }: AxiosResponse<FindSalesJournalResponse> = yield call(
      SalesApi.findSalesJournal,
      findSalesJournalRequestMap(filters, 'gcs')
    );
    yield put(findSalesJournalGcsSuccess(findSalesJournalGcsResponseMap(data)));
  } catch (error) {
    yield put(findSalesJournalGcsFailure(error as HttpRequestError));
  }
}

function* findSalesJournalNcs() {
  const filters: ReportFilter = yield select(reportFilterSelector);
  try {
    const { data }: AxiosResponse<FindSalesJournalResponse> = yield call(
      SalesApi.findSalesJournal,
      findSalesJournalRequestMap(filters, 'ncs')
    );
    yield put(findSalesJournalNcsSuccess(findSalesJournalNcsResponseMap(data)));
  } catch (error) {
    yield put(findSalesJournalNcsFailure(error as HttpRequestError));
  }
}

function* findSalesJournalFcs() {
  const filters: ReportFilter = yield select(reportFilterSelector);
  try {
    const { data }: AxiosResponse<FindSalesJournalResponse> = yield call(
      SalesApi.findSalesJournal,
      findSalesJournalRequestMap(filters, 'fcs')
    );
    yield put(findSalesJournalFcsSuccess(findSalesJournalFcsResponseMap(data)));
  } catch (error) {
    yield put(findSalesJournalFcsFailure(error as HttpRequestError));
  }
}

function* findSalesJournalThirdParty() {
  const filters: ReportFilter = yield select(reportFilterSelector);
  try {
    const { data }: AxiosResponse<FindSalesJournalResponse> = yield call(
      SalesApi.findSalesJournal,
      findSalesJournalRequestMap(filters, 'thirdparty')
    );
    yield put(findSalesJournalThirdPartySuccess(findSalesJournalThirdPartyResponseMap(data)));
  } catch (error) {
    yield put(findSalesJournalThirdPartyFailure(error as HttpRequestError));
  }
}

function* findSaleDiscounts() {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const gcsFilters: GcsFilter = yield select(reportGcsFilterSelector);
  if (!filters.countryCode || noValidDateRanges(filters) || hasIncompleteRanges(filters)) return;
  yield put(findSaleDiscountsRequested());
  try {
    const { data }: AxiosResponse<FindDiscountsResponse> = yield call(
      SalesParametersApi.findSaleDiscounts,
      findDiscountsRequestMap(filters, gcsFilters)
    );
    yield put(findSaleDiscountsSuccess(findDiscountsResponseMap(data)));
  } catch (error) {
    yield put(findSaleDiscountsFailure(error as HttpRequestError));
  }
}

function* findItemDiscounts() {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const gcsFilters: GcsFilter = yield select(reportGcsFilterSelector);
  if (!filters.countryCode || noValidDateRanges(filters) || hasIncompleteRanges(filters)) return;
  yield put(findItemDiscountsRequested());
  try {
    const { data }: AxiosResponse<FindDiscountsResponse> = yield call(
      SalesParametersApi.findItemDiscounts,
      findDiscountsRequestMap(filters, gcsFilters)
    );
    yield put(findItemDiscountsSuccess(findDiscountsResponseMap(data)));
  } catch (error) {
    yield put(findItemDiscountsFailure(error as HttpRequestError));
  }
}

function* findMastersImport() {
  const filters: MastersImportDetailFilter = yield select(reportMastersImportFilterSelector);
  const { countries }: MasterData = yield select(masterDataDataSelector);
  try {
    const { data }: AxiosResponse<FindMastersImportResponse> = yield call(
      SalesApi.findMastersImport,
      findMastersImportRequestMap(filters)
    );
    yield put(findMastersImportSuccess(findMastersImportResponseMap(data, countries)));
  } catch (error) {
    yield put(findMastersImportFailure(error as HttpRequestError));
  }
}

function* findMastersTypes() {
  try {
    const { data }: AxiosResponse<FindMastersTypesResponse> = yield call(SalesApi.findMastersTypes);
    yield put(findMastersTypesSuccess(data));
  } catch (error) {
    yield put(findMastersTypesFailure(error as HttpRequestError));
  }
}

function* mastersImportExecution({ payload }: MastersImportExecutionRequestedAction) {
  try {
    yield call(SalesApi.mastersImportExecution, findMastersImportExecutionRequestMap(payload));
    yield put(mastersImportExecutionSuccess());
  } catch (error) {
    yield put(mastersImportExecutionFailure(error as HttpRequestError));
  }
}

function* mastersImportExecutionSuccessNotification() {
  yield put(
    enqueueNotification({
      key: 'mastersImportExecutionSucces',
      message: t => t('masters-import.execution.message-success'),
      variant: 'success',
    })
  );
}

function* findInterfaces(_: FindInterfacesRequestedAction) {
  const interfacesFilters: InterfacesFilter = yield select(reportInterfacesFilterSelector);
  const country: string = yield select(reportFilterCountryCodeSelector);
  const stores: Stores[] = yield select(reportFilterStoresSelector);
  const startDate: DateValue = yield select(reportFilterStartBusinessDateSelector);
  const endDate: DateValue = yield select(reportFilterEndBusinessDateSelector);
  const filters: InterfacesFilter = {
    ...interfacesFilters,
    country,
    stores,
    startDate,
    endDate,
  };
  try {
    const { data }: AxiosResponse<FindInterfacesResponse> = yield call(
      SalesApi.findInterfaces,
      findInterfacesRequestMap(filters)
    );
    yield put(findInterfacesSuccess(findInterfacesResponseMap(data)));
  } catch (error) {
    yield put(findInterfacesFailure(error as HttpRequestError));
  }
}

function* findPendingInterfaceSales({
  startDate,
  endDate,
}: FindPendingInterfacesSalesRequestedAction) {
  try {
    const filters: PendingInterfacesFilter = yield select(reportPendingInterfacesFilterSelector);
    const start = startDate || (filters.startBusinessDate.accept as Date);
    const end = endDate || (filters.endBusinessDate.accept as Date);

    const { data }: AxiosResponse<FindPendingInterfacesResponse> = yield call(
      SalesApi.findPendingInterface,
      findPendingInterfacesRequestMap(filters)
    );

    yield put(
      findPendingInterfacesSalesSuccess(findPendingInterfacesResponseMap(data), start, end)
    );
  } catch (error) {
    yield put(findPendingInterfacesSalesFailure(error as HttpRequestError));
  }
}

function* createPendingInterfaces({ payload }: PendingInterfacesCreateRequestedAction) {
  try {
    yield call(SalesApi.createPendingInterfaces, createPendingInterfacesRequestMap(payload));
    yield put(pendingInterfacesCreateSuccess());
  } catch (error) {
    yield put(pendingInterfacesCreateFailure(error as HttpRequestError));
  }
}

function* executefindPendingInterface() {
  try {
    const filters: ExecutionStatusPendingInterfacesFilter = yield select(
      reportExecutePendingInterfacesFilterSelector
    );

    const { data }: AxiosResponse<ExecutionPendingInterfacesResponse> = yield call(
      SalesApi.executionsPendingInterfaces,
      executionPendingInterfacesRequestMap(filters)
    );

    yield put(executePendingInterfacesSuccess(executionPendingInterfacesResponseMap(data)));
  } catch (error) {
    yield put(executePendingInterfacesFailure(error as HttpRequestError));
  }
}

function* getParameters() {
  let pendinInterfacesParameters = AppStorage.getPendingInterfacesParameters();

  try {
    if (!pendinInterfacesParameters) {
      const { data }: AxiosResponse<FindPendingInterfacesParametersResponse> = yield call(
        SalesApi.findPendingInterfacesParameters
      );
      pendinInterfacesParameters = findPendingInterfacesParametersResponseMap(data);
      AppStorage.setPendingInterfacesParameters(pendinInterfacesParameters);
    }
    yield put(executePendingInterfacesParametersSuccess(pendinInterfacesParameters));
  } catch (error) {
    yield put(executePendingInterfacesParametersFailure(error as HttpRequestError));
  }
}

function* getInterfacesParameters() {
  let interfacesParameters: InterfacesParameters | null = AppStorage.getInterfacesParameters();

  try {
    if (!interfacesParameters) {
      const { data }: AxiosResponse<string[]> = yield call(SalesParametersApi.findInterfaceName);
      interfacesParameters = { interfaceNames: data };
      AppStorage.setInterfacesParameters(interfacesParameters);
    }
    yield put(findInterfacesParametersSuccess(interfacesParameters));
  } catch (error) {
    yield put(findInterfacesParametersFailure(error as HttpRequestError));
  }
}

export default [
  takeLatest(Object.keys(ANALYTICS_OPENED_ACTIONS), trackOpened),
  takeLatest(Object.keys(ANALYTICS_SUCCESS_ACTIONS), trackSuccess),
  takeLatest(Object.keys(ANALYTICS_FAILURE_ACTIONS), trackFailure),
  takeLatest(
    [
      ActionType.changeFiltersStore,
      ActionType.changeFilterStartAccountingDate,
      ActionType.changeFilterEndAccountingDate,
      ActionType.changeFilterStartBusinessDate,
      ActionType.changeFilterEndBusinessDate,
      ActionType.changeFilterStartOperationDate,
      ActionType.changeFilterEndOperationDate,
    ],
    findPos
  ),
  takeLatest(
    [ActionType.changeFiltersStore, ActionType.changeFilterEndBusinessDate],
    findOperatorDlv
  ),
  takeLatest(ActionType.changeFiltersCountry, findSalesTypes),
  takeLatest(ActionType.changeFiltersCountry, findPaymentType),
  takeLatest(ActionType.changeFiltersCountry, findTimeBand),
  takeLatest(ActionType.changeFiltersCountry, findTransactionType),
  takeLatest(ActionType.findSalesRequested, findSales),
  takeLatest(ActionType.findGcsRequested, findGcs),
  takeLatest(ActionType.findGcsByPaymentTypesRequested, findGcsByPaymentTypes),
  takeLatest(ActionType.findGcsByTaxRequested, findGcsByTax),
  takeLatest(ActionType.findClosingSalesRequested, findClosingSales),
  takeLatest(ActionType.findClosingCollectionsRequested, findClosingCollections),
  takeLatest(ActionType.findMissingClosingSalesCausesRequested, findMissingClosingSalesCauses),
  takeLatest(
    ActionType.findMissingClosingCollectionsCausesRequested,
    findMissingClosingCollectionsCauses
  ),
  takeLatest(ActionType.findMissingControlErrorsRequested, findMissingControlErrors),
  takeLatest(ActionType.findMissingClosingTotalRequested, findMissingClosingTotal),
  takeLatest(ActionType.findReportTenderRequested, findTenderReport),
  takeLatest(ActionType.findReliefsRequested, findReliefs),
  takeLatest(ActionType.findCancellationsRequested, findCancellations),
  takeLatest(ActionType.findOperationTypesRequested, findOperationTypes),
  takeLatest(ActionType.findSalesByProductRequested, findSalesByProduct),
  takeLatest(ActionType.findGCsByProductRequested, findGCsByProduct),
  takeLatest(ActionType.findThirdPartySalesByItemsRequested, findThirdPartySalesByItems),
  takeLatest(ActionType.findPettyCashDifferenceRequested, findPettyCashDifference),
  takeLatest(
    ActionType.findPettyCashDifferenceByPaymentTypeRequested,
    findPettyCashDifferenceByPaymentType
  ),
  takeLatest(ActionType.findEconomicAccounitngReportRequested, findEconomicAccountingReport),
  takeLatest(ActionType.findCashMovementReportRequested, findCashMovementReport),
  takeLatest(ActionType.findCashCountReportRequested, findCashCountReport),
  takeLatest(ActionType.findCashDiscrepancyReportRequested, findCashDiscrepancyReport),
  takeLatest(ActionType.findExchangePurchaseReportRequested, findExchangePurchaseReport),
  takeLatest(ActionType.findFixedFundReportRequested, findFixedFundReport),
  takeLatest(ActionType.findPettyCashWithdrawalReportRequested, findPettyCashWithdrawalReport),
  takeLatest(ActionType.findExtraCashReportRequested, findExtraCashReport),
  takeLatest(ActionType.findRecoveryReportRequested, findRecoveryReport),
  takeLatest(ActionType.findEnvelopeDetailsReportRequested, findEnvelopeDetailsReport),
  takeLatest(ActionType.findSalesJournalRequested, findSalesJournalGcs),
  takeLatest(ActionType.findSalesJournalRequested, findSalesJournalNcs),
  takeLatest(ActionType.findSalesJournalRequested, findSalesJournalFcs),
  takeLatest(ActionType.findSalesJournalRequested, findSalesJournalThirdParty),
  takeLatest(
    [ActionType.changeFiltersCountry, ActionType.changeFiltersStore, ...DATE_FILTER_ACTION],
    findSaleDiscounts
  ),
  takeLatest(
    [ActionType.changeFiltersCountry, ActionType.changeFiltersStore, ...DATE_FILTER_ACTION],
    findItemDiscounts
  ),
  takeLatest(ActionType.findMastersImportRequested, findMastersImport),
  takeLatest(ActionType.findMastersTypesRequested, findMastersTypes),
  takeLatest(ActionType.mastersImportExecutionRequested, mastersImportExecution),
  takeLatest(ActionType.mastersImportExecutionSuccess, mastersImportExecutionSuccessNotification),
  takeLatest(ActionType.findCauseTypesRequested, findCauseTypes),
  takeLatest(ActionType.findInterfacesParametersRequested, getInterfacesParameters),
  takeLatest(ActionType.findInterfacesRequested, findInterfaces),
  takeLatest(ActionType.findPendingInterfacesSalesRequested, findPendingInterfaceSales),
  takeLatest(ActionType.pendingInterfacesCreateRequested, createPendingInterfaces),
  takeLatest(ActionType.executePendingInterfacesRequested, executefindPendingInterface),
  takeLatest(ActionType.executePendingInterfacesParametersRequested, getParameters),
];
